import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useNavGuard } from "@/contexts/NavigationContext";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { fmtL, apiErr, descripcionComercial } from "@/lib/format";
import { toast } from "sonner";
import { PlusIcon, TrashIcon, ArrowLeftIcon, LockIcon, TruckIcon, MagnifyingGlassIcon, XIcon, WarningIcon, HandIcon, LockKeyIcon, LockOpenIcon, PackageIcon } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import ProductSearch from "@/components/ProductSearch";

const PRICE_LEVELS = ["p0", "p1", "p2", "p3", "p4"];

/**
 * Regla única de nivel de precio (idéntica al backend `resolve_price_level`):
 *   override por producto del cliente > «Todos los demás» (o nivel del cliente) > primer nivel no vacío P1..P4
 */
export function resolvePriceLevel(prod, nivelOverride, nivelCliente) {
  for (const cand of [nivelOverride, nivelCliente]) {
    const c = (cand || "").toLowerCase();
    if (PRICE_LEVELS.includes(c) && Number(prod[`precio_${c}`]) > 0) return { nivel: c, precio: Number(prod[`precio_${c}`]) };
  }
  for (const c of ["p1", "p2", "p3", "p4"]) {
    if (Number(prod[`precio_${c}`]) > 0) return { nivel: c, precio: Number(prod[`precio_${c}`]) };
  }
  return { nivel: (nivelOverride || nivelCliente || "p4").toLowerCase(), precio: 0 };
}


/**
 * Buscador de producto con typeahead — filtra por nombre/código/SKU en vivo.
 * Se usa en cada línea de la proforma para reemplazar el <select> nativo que
 * es intragable con catálogos grandes.
 */


/**
 * Buscador de cliente con typeahead — filtra por nombre/código/RTN en vivo.
 * Reemplaza el <select> nativo para catálogos grandes de clientes.
 */
function ClientSearch({ clients, value, onPick, testid }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(0);
  const wrapRef = useRef(null);
  const [dropdownStyle, setDropdownStyle] = useState({ display: "none" });
  const selected = clients.find((c) => c.id === value) || null;

  const filtered = useMemo(() => {
    if (!q.trim()) return clients.slice(0, 30);
    const s = q.trim().toLowerCase();
    return clients
      .filter((c) =>
        (c.nombre || "").toLowerCase().includes(s) ||
        (c.codigo || "").toLowerCase().includes(s) ||
        (c.rtn || "").toLowerCase().includes(s) ||
        (c.nombre_sistema || "").toLowerCase().includes(s)
      )
      .slice(0, 40);
  }, [q, clients]);

  useEffect(() => {
    if (!open || !wrapRef.current) { setDropdownStyle({ display: "none" }); return; }
    const rect = wrapRef.current.getBoundingClientRect();
    setDropdownStyle({
      position: "fixed",
      top: rect.bottom + 4,
      left: rect.left,
      width: Math.max(rect.width, 420),
      maxHeight: 340,
      zIndex: 60,
    });
  }, [open, q, filtered.length]);

  const pick = (c) => { onPick(c.id); setQ(""); setOpen(false); };

  if (selected && !open) {
    return (
      <div className="flex items-center gap-2 bg-agro-green/10 border border-agro-green/40 rounded-md px-3 py-2" data-testid={testid}>
        <span className="font-mono text-[11px] text-agro-green">{selected.codigo}</span>
        <span className="text-sm text-text-primary flex-1 truncate">{selected.nombre}</span>
        {selected.exonerado && <span className="text-[10px] bg-agro-yellow/20 text-agro-yellow border border-agro-yellow/40 rounded px-1.5 py-0.5">EXO</span>}
        <button type="button" onClick={() => { setOpen(true); setQ(""); }}
                data-testid={`${testid}-change`}
                className="text-[11px] text-text-muted hover:text-agro-yellow underline shrink-0">cambiar</button>
      </div>
    );
  }

  return (
    <div className="relative" ref={wrapRef} data-testid={testid}>
      <div className="flex items-center bg-app border border-border rounded-md focus-within:border-agro-green">
        <MagnifyingGlassIcon size={14} className="text-text-muted ml-3" />
        <input
          type="text"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); setHover(0); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 200)}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") { e.preventDefault(); setHover((h) => Math.min(h + 1, filtered.length - 1)); }
            else if (e.key === "ArrowUp") { e.preventDefault(); setHover((h) => Math.max(h - 1, 0)); }
            else if (e.key === "Enter" && filtered[hover]) { e.preventDefault(); pick(filtered[hover]); }
            else if (e.key === "Escape") setOpen(false);
          }}
          placeholder="Buscar cliente por nombre, código o RTN…"
          data-testid={`${testid}-input`}
          className="w-full bg-transparent px-3 py-2 text-sm focus:outline-none"
          autoComplete="off"
        />
        {q && (
          <button aria-label="Cerrar" type="button" onClick={() => setQ("")} className="text-text-muted hover:text-text-primary px-2">
            <XIcon size={12} />
          </button>
        )}
      </div>
      {open && (
        <div style={dropdownStyle}
             className="bg-card border border-border rounded-md shadow-xl overflow-y-auto"
             data-testid={`${testid}-list`}
             onMouseDown={(e) => e.preventDefault()}>
          {filtered.length === 0 ? (
            <div className="px-3 py-3 text-xs text-text-muted">No hay clientes que coincidan</div>
          ) : filtered.map((c, k) => (
            <button
              type="button"
              key={c.id}
              onClick={() => pick(c)}
              onMouseEnter={() => setHover(k)}
              data-testid={`${testid}-opt-${c.codigo}`}
              className={`w-full text-left px-3 py-2 flex items-center gap-2 border-b border-border/40 last:border-0 ${k === hover ? "bg-white/[0.04]" : "hover:bg-white/[0.02]"}`}
            >
              <span className="font-mono text-[10px] text-agro-green shrink-0">{c.codigo}</span>
              <span className="text-sm text-text-primary flex-1 truncate">{c.nombre}</span>
              {c.exonerado && <span className="text-[10px] bg-agro-yellow/20 text-agro-yellow border border-agro-yellow/40 rounded px-1.5 py-0.5 shrink-0">EXO</span>}
              {c.rtn && <span className="text-[10px] text-text-muted shrink-0 font-mono">{c.rtn}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


export default function InvoiceNew({ corregir = false }) {
  const navigate = useNavigate();
  // PRD: flujo estricto — TODO documento comercial nace como proforma.
  const tipo = "proforma";
  // Edición de una proforma en borrador o devuelta para corrección (flujo de aprobación del RTC)
  const { id: editId } = useParams();
  const [editando, setEditando] = useState(null);
  const [motivoCorreccion, setMotivoCorreccion] = useState("");   // corregir factura (mismo número)
  // Proforma precargada desde una OPORTUNIDAD SUGERIDA del calendario (se revisa antes de guardar)
  const prefill = useLocation().state?.prefill || null;
  const [prefillPaso, setPrefillPaso] = useState(prefill ? "cliente" : "listo");

  const { user } = useAuth();
  // Delegado / Administrador General: tarifa por línea (P1–P4), P0 autorizado y plazos mayores (queda en la bitácora)
  // Precio / tarifa de la línea (solo este documento): Administrador General y Delegado. El Delegado justifica todo
  // cambio con una nota; todo P0 (precio inferior a P1) lleva nota para ambos.
  const esAdminGeneral = user?.role === "admin" || Boolean(user?.effective?.is_admin);
  const puedeTarifa = esAdminGeneral || Boolean(user?.effective?.revisiones?.proformas);
  const puedePlazo = puedeTarifa;
  const canOverridePrice = puedeTarifa;
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [reps, setReps] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [clienteId, setClienteId] = useState("");
  const [formaPago, setFormaPago] = useState("contado");
  const [plazo, setPlazo] = useState("");                 // crédito: plazo en días (vacío = el del cliente)
  const [tipoVenta, setTipoVenta] = useState("directa");
  const [vendedor, setVendedor] = useState("");
  const [vendedorRtcId, setVendedorRtcId] = useState("");
  // región se deriva automáticamente del cliente; sin input manual
  const [cuentaCxc, setCuentaCxc] = useState("1103");
  const [codigoPromocional, setCodigoPromocional] = useState("");
  // Campos fiscales extra PRD 5.2 — algunos pre-rellenados desde el cliente
  const [ocExenta, setOcExenta] = useState("");
  const [constanciaExo, setConstanciaExo] = useState("");
  const [registroSag, setRegistroSag] = useState("");
  // Guía de remisión PRD 5.4
  const [imprimirGuia, setImprimirGuia] = useState(false);
  const [destinoEnvioId, setDestinoEnvioId] = useState("");
  const [destinosCliente, setDestinosCliente] = useState([]);
  // 2.5 — destino nuevo capturado desde la factura
  const [nuevoDestinoOpen, setNuevoDestinoOpen] = useState(false);
  const [nuevoDestino, setNuevoDestino] = useState(null);
  // 2.1 — envío automático por correo (default = preferencia del cliente)
  const [enviarCorreo, setEnviarCorreo] = useState(false);
  // 2 — overrides de precio por producto del cliente + 2.4 balance de crédito
  const [priceOverrides, setPriceOverrides] = useState({});
  const [balance, setBalance] = useState(null);
  // CLI-01 — proyecto/subcuenta del cliente (opcional)
  const [proyectoId, setProyectoId] = useState("");
  const [proyectosCliente, setProyectosCliente] = useState([]);
  // VEN-01 — Dependiente de tienda
  const [dependienteId, setDependienteId] = useState("");
  const [empleados, setEmpleados] = useState([]);
  const [fechaVenc, setFechaVenc] = useState("");
  const [notas, setNotas] = useState("");
  // Proforma: vigencia de la oferta y condiciones comerciales (valores iniciales = Datos de facturación)
  const [vigencia, setVigencia] = useState("");
  const [condiciones, setCondiciones] = useState("");
  useEffect(() => {
    if (editId) return;                 // al editar se conservan la vigencia y condiciones de la proforma
    api.get("/settings/company").then(({ data }) => {
      const pf = data?.documentos?.proforma || {};
      const dias = Number(pf.vigencia_dias || 0);
      if (dias > 0) {
        const d = new Date(); d.setDate(d.getDate() + dias);
        setVigencia(d.toISOString().slice(0, 10));
      }
      setCondiciones(pf.condiciones || "");
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!editId) return;
    api.get(`/invoices/${editId}`).then(({ data }) => {
      setEditando(data);
      setClienteId(data.cliente_id);
      setFormaPago(data.forma_pago || "contado");
      if (data.plazo_credito_dias) setPlazo(String(data.plazo_credito_dias));
      setNotas(data.notas || "");
      if (data.vigencia_oferta) setVigencia(String(data.vigencia_oferta).slice(0, 10));
      setCondiciones(data.condiciones_comerciales || "");
      if (data.fecha_vencimiento) setFechaVenc(String(data.fecha_vencimiento).slice(0, 10));
      setItems((data.items || []).map((it, idx) => ({
        producto_id: it.producto_id, codigo: it.codigo, descripcion: it.descripcion, cantidad: it.cantidad,
        precio_unitario: it.precio_unitario, descuento: it.descuento || 0, isv_pct: it.isv_pct ?? 15,
        nivel_aplicado: it.nivel_aplicado || "", precio_lista: it.precio_tarifa_cliente || it.precio_lista,
        nivel_lista: it.nivel_cliente || it.nivel_aplicado, nota_precio: it.nota_precio || "",
        p0_manual: it.nivel_aplicado === "p0", fecha_ingreso: "", cantidad_disponible: 0, peps_breakdown: null,
        // el lote elegido se conserva al editar o corregir (si no, el inventario volvería a descontar por PEPS)
        entry_id: it.entry_id_manual || null, lote_manual: Boolean(it.entry_id_manual),
        lote: ((data.lotes_lineas || [])[idx] || [])[0] || "",
      })));
    }).catch((e) => toast.error(apiErr(e)));
  }, [editId]);

  useEffect(() => {
    (async () => {
      const [cli, prd, rp, acc, emps] = await Promise.all([
        api.get("/clients"),
        api.get("/products"),
        api.get("/representatives").catch(() => ({ data: [] })),
        api.get("/accounting/chart").catch(() => ({ data: [] })),
        api.get("/hr/directory").catch(() => ({ data: [] })),
      ]);
      setClients(cli.data);
      setProducts((prd.data || []).filter((p) => !p.es_flete));   // el Flete es un servicio de compras: no se vende
      setReps(rp.data || []);
      setAccounts((acc.data || []).filter((a) => a.codigo?.startsWith("110") && a.tipo === "activo" && a.es_movimiento));
      setEmpleados((emps.data || []).filter((e) => e.estado === "activo"));
    })();
  }, []);

  // Cargar solo destinos del cliente cuando cambia. El resto (forma_pago, RTC, etc.)
  // se autopopula en el onChange del cliente para evitar lint warnings con setState en effect.
  useEffect(() => {
    if (!clienteId) { setDestinosCliente([]); setDestinoEnvioId(""); setProyectosCliente([]); setProyectoId(""); setPriceOverrides({}); setBalance(null); return; }
    api.get(`/clients/${clienteId}/price-overrides`)
      .then(({ data }) => setPriceOverrides(Object.fromEntries((data || []).map((o) => [o.producto_id, (o.nivel_precio || "").toLowerCase()]))))
      .catch(() => setPriceOverrides({}));
    api.get(`/clients/${clienteId}/balance`).then(({ data }) => setBalance(data)).catch(() => setBalance(null));
    api.get(`/clients/${clienteId}/shipping-destinations`)
      .then(({ data }) => {
        setDestinosCliente(data || []);
        if (data?.length === 1) setDestinoEnvioId(data[0].id);
      })
      .catch(() => setDestinosCliente([]));
    api.get(`/clients/${clienteId}/projects`)
      .then(({ data }) => setProyectosCliente(data || []))
      .catch(() => setProyectosCliente([]));
  }, [clienteId]);

  // Handler que aplica todos los defaults derivados del cliente
  const onClientChange = (newId) => {
    setClienteId(newId);
    const cli = clients.find((c) => c.id === newId);
    if (!cli) return;
    setFormaPago(cli.dias_credito > 0 ? "credito" : "contado");
    if (cli.rtc_asignado_id && reps.some((r) => r.id === cli.rtc_asignado_id)) {
      setTipoVenta("rtc");
      setVendedorRtcId(cli.rtc_asignado_id);
      setVendedor(cli.rtc_asignado_nombre || "");
    } else {
      setTipoVenta("directa");
      setVendedorRtcId("");
      setVendedor(cli.vendedor_asignado || user?.name || "");
    }
    setEnviarCorreo(Boolean(cli.email) && cli.notificar_correo !== false);
    setNuevoDestino(null);
    if (cli.exonerado) setConstanciaExo(cli.registro_exonerado || "");
    if (cli.registro_sag) setRegistroSag(cli.registro_sag);
    if (cli.dias_credito > 0) {
      const v = new Date();
      v.setDate(v.getDate() + cli.dias_credito);
      setFechaVenc(v.toISOString().slice(0, 10));
      setPlazo(String(cli.dias_credito));
    } else {
      setFechaVenc("");
      setPlazo("30");
    }
  };

  // Precarga en dos pasos: 1) cliente (aplica sus condiciones)  2) productos con precio VIGENTE del cliente
  useEffect(() => {
    if (prefillPaso === "cliente" && clients.length && products.length) {
      onClientChange(prefill.cliente_id);
      if (prefill.nota) setNotas(prefill.nota);
      setPrefillPaso("productos");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillPaso, clients, products]);
  useEffect(() => {
    if (prefillPaso !== "productos" || clienteId !== prefill.cliente_id) return;
    const t = setTimeout(() => {
      const validos = (prefill.items || []).filter((it) => products.some((p) => p.id === it.producto_id));
      setItems(validos.map((it) => ({ producto_id: "", codigo: "", descripcion: "", cantidad: it.cantidad, precio_unitario: 0, descuento: 0,
        isv_pct: 15, fecha_ingreso: "", lote: "", cantidad_disponible: 0, peps_breakdown: null })));
      validos.forEach((it, i) => pickProduct(i, it.producto_id));
      setPrefillPaso("listo");
    }, 700);                      // espera los precios especiales del cliente
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillPaso, clienteId, priceOverrides]);

  const cliente = clients.find((c) => c.id === clienteId);
  const exonerado = cliente?.exonerado || false;
  // productos sin asignación explícita: «Todos los demás» (Precios por producto) o, si no se configuró, el nivel del cliente
  const nivelCliente = (cliente?.nivel_resto || cliente?.nivel_precio || "").toLowerCase();
  const region = cliente?.region || "";

  const addRow = () => setItems([...items, { producto_id: "", codigo: "", descripcion: "", cantidad: 1, precio_unitario: 0, descuento: 0, isv_pct: 15, fecha_ingreso: "", lote: "", cantidad_disponible: 0, peps_breakdown: null }]);

  const updateRow = (i, patch) => {
    setItems((prev) => {
      const next = [...prev];
      next[i] = { ...next[i], ...patch };
      return next;
    });
  };

  const refreshPepsBreakdown = async (i, producto_id, cantidad) => {
    if (!producto_id || !cantidad || Number(cantidad) <= 0) return;
    try {
      const { data } = await api.get(`/inventory/peps-preview/${producto_id}`, { params: { cantidad: Number(cantidad) } });
      updateRow(i, { peps_breakdown: data });
    } catch { /* no-op */ }
  };

  const pickProduct = async (i, pid) => {
    const p = products.find((x) => x.id === pid);
    if (!p) return;
    // Precio: override por producto > nivel del cliente > primer nivel no vacío P1..P4 (misma regla que backend)
    const { nivel: nivelAplicado, precio: nivelPrice } = resolvePriceLevel(p, priceOverrides[p.id], nivelCliente);
    // Respeta el ISV del producto — incluye 0% (Exento). Sólo cae a 15% si el producto no tiene isv definido.
    const productIsv = (p.isv === undefined || p.isv === null || p.isv === "") ? 15 : Number(p.isv);
    updateRow(i, {
      producto_id: p.id, codigo: p.codigo,
      producto_nombre: p.nombre || "",
      // la descripción de la línea sale del NOMBRE ACTUAL del producto (su perfil) + el lote elegido
      descripcion: p.nombre || descripcionComercial(p.descripcion) || `${p.categoria} - ${p.sku || p.codigo}`,
      precio_unitario: nivelPrice, isv_pct: productIsv,
      precio_lista: nivelPrice, nivel_lista: nivelAplicado,
      nivel_aplicado: nivelAplicado, p0_manual: false,
      fecha_ingreso: "", lote: "", cantidad_disponible: 0, peps_breakdown: null,
      lote_manual: null,
      entry_id: null,
      lotes_disponibles: null,
    });
    // Helper: solo aplica update si la línea sigue apuntando al mismo producto (evita race)
    const setIfSameProduct = (patch) => {
      setItems((prev) => {
        if (!prev[i] || prev[i].producto_id !== p.id) return prev;
        const next = [...prev];
        next[i] = { ...next[i], ...patch };
        return next;
      });
    };
    // Consulta el lote más viejo (PEPS)
    try {
      const { data } = await api.get(`/inventory/oldest-lot/${p.id}`);
      if (data?.found) {
        setIfSameProduct({
          descripcion: data.descripcion_venta || p.nombre || data.descripcion,
          fecha_ingreso: data.fecha_ingreso || "",
          lote: data.lote || data.batch || "",
          cantidad_disponible: data.cantidad_restante || 0,
          entry_id: data.entry_id || null,
        });
      }
    } catch { /* ignore — el backend igualmente hace PEPS al despachar */ }
    // Precarga la lista de lotes disponibles
    try {
      const { data } = await api.get(`/inventory/lots/${p.id}`);
      setIfSameProduct({ lotes_disponibles: data.lotes || [] });
    } catch { /* no-op */ }
    // Y dispara el desglose PEPS con la cantidad actual
    try {
      const curQty = Number(items[i]?.cantidad || 1);
      if (curQty > 0) {
        const { data } = await api.get(`/inventory/peps-preview/${p.id}`, { params: { cantidad: curQty } });
        setIfSameProduct({ peps_breakdown: data });
      }
    } catch { /* no-op */ }
  };

  const chooseLot = (i, lot) => {
    // El usuario elige manualmente un lote — se marca como lote_manual. La descripción lleva ESE lote (no el más antiguo).
    const marcas = [lot.lote_fabricacion && `LOTE: ${lot.lote_fabricacion}`, lot.batch && `BATCH: ${lot.batch}`].filter(Boolean).join("; ");
    const base = items[i]?.producto_nombre || descripcionComercial(items[i]?.descripcion || "");
    updateRow(i, {
      descripcion: lot.descripcion_venta || (marcas ? `${base} ${marcas}` : base),
      lote: lot.lote || lot.batch || "",
      fecha_ingreso: lot.fecha_ingreso || "",
      cantidad_disponible: lot.cantidad_restante || 0,
      entry_id: lot.entry_id || null,
      lote_manual: true,
      peps_breakdown: null,
      show_lot_picker: false,
    });
  };

  const toggleLotPicker = (i) => {
    updateRow(i, { show_lot_picker: !items[i].show_lot_picker });
  };

  // Cuando cambia la cantidad SIEMPRE recalcula el desglose PEPS para mostrar
  // cuántas unidades salen de cada lote (aunque quepa en el lote más viejo).
  const changeQuantity = (i, value) => {
    updateRow(i, { cantidad: value });
    const row = items[i];
    if (!row || !row.producto_id) return;
    const q = Number(value);
    if (q > 0 && !row.lote_manual) {
      refreshPepsBreakdown(i, row.producto_id, q);
    } else {
      updateRow(i, { peps_breakdown: null });
    }
  };

  const removeRow = (i) => setItems(items.filter((_, idx) => idx !== i));

  // ¿La línea cambió respecto a la tarifa del cliente? ¿Requiere nota?
  const p1De = (row) => Number(products.find((x) => x.id === row.producto_id)?.precio_p1 || 0);
  const lineaCambiada = (row) => row.p0_manual || (row.nivel_aplicado || "") !== (row.nivel_lista || "")
    || (Number(row.precio_lista) > 0 && Math.abs(Number(row.precio_unitario) - Number(row.precio_lista)) > 0.009);
  const requiereNota = (row) => Boolean(row.producto_id) && (row.p0_manual || (lineaCambiada(row) && !esAdminGeneral));
  // Precio escrito a mano: si queda por debajo de la tarifa P1 del producto, la línea es P0
  const cambiarPrecio = (i, v) => {
    const row = items[i];
    const p1 = p1De(row);
    const n = Number(v);
    if (p1 > 0 && n > 0 && n < p1 - 0.005) updateRow(i, { precio_unitario: v, p0_manual: true, nivel_aplicado: "p0" });
    else if (row.p0_manual && p1 > 0 && n >= p1 - 0.005) updateRow(i, { precio_unitario: v, p0_manual: false, nivel_aplicado: row.nivel_lista || nivelCliente });
    else updateRow(i, { precio_unitario: v });
  };
  // Tarifa de la línea (Delegado / Administrador General): el precio es el de esa tarifa del producto
  const cambiarTarifa = (i, nivel) => {
    const row = items[i];
    const p = products.find((x) => x.id === row.producto_id);
    const precio = Number(p?.[`precio_${nivel}`] || 0);
    if (!(precio > 0)) { toast.error(`El producto no tiene precio en la tarifa ${nivel.toUpperCase()}`); return; }
    updateRow(i, { nivel_aplicado: nivel, precio_unitario: precio, p0_manual: false });
  };
  // Plazo de crédito → fecha de vencimiento
  const elegirPlazo = (dias) => {
    setPlazo(dias);
    const v = new Date();
    v.setDate(v.getDate() + Number(dias || 0));
    setFechaVenc(v.toISOString().slice(0, 10));
  };
  const diasAutorizados = Number(cliente?.dias_credito || 0);
  const opcionesPlazo = [...new Set([diasAutorizados, 8, 15, 30, 45, 60, 90, 120].filter((d) => d > 0))].sort((a, b) => a - b);

  // P0 — precio de negocio especial capturado en la línea (solo con permiso)
  const toggleP0 = (i) => {
    const row = items[i];
    if (row.p0_manual) updateRow(i, { p0_manual: false, nivel_aplicado: row.nivel_lista || nivelCliente, precio_unitario: row.precio_lista || row.precio_unitario });
    else updateRow(i, { p0_manual: true, nivel_aplicado: "p0" });
  };

  const totals = useMemo(() => {
    let s15 = 0, s18 = 0, sx = 0, sxo = 0, i15 = 0, i18 = 0, td = 0, total = 0;
    items.forEach((it) => {
      const bruto = (Number(it.cantidad) || 0) * (Number(it.precio_unitario) || 0);
      const neto = bruto - (Number(it.descuento) || 0);
      td += Number(it.descuento) || 0;
      if (exonerado) { sxo += neto; total += neto; }
      else if (Number(it.isv_pct) === 15) { s15 += neto; i15 += neto * 0.15; total += neto * 1.15; }
      else if (Number(it.isv_pct) === 18) { s18 += neto; i18 += neto * 0.18; total += neto * 1.18; }
      else { sx += neto; total += neto; }
    });
    return { s15, s18, sx, sxo, i15, i18, td, total, subtotal: s15 + s18 + sx + sxo };
  }, [items, exonerado]);

  // 2.4 — alerta de límite de crédito (>= 80% o lo supera con esta venta)
  const creditAlert = useMemo(() => {
    if (!balance || !(balance.limite_credito > 0) || formaPago !== "credito") return null;
    const proyectado = (balance.saldo_total || 0) + totals.total;
    const pct = Math.round((proyectado / balance.limite_credito) * 1000) / 10;
    if (pct < 80) return null;
    return { pct, proyectado, excede: proyectado > balance.limite_credito };
  }, [balance, totals.total, formaPago]);

  const save = async () => {
    if (!clienteId) return toast.error("Selecciona un cliente");
    if (items.length === 0) return toast.error("Agrega al menos un producto");
    if (items.some((it) => !it.producto_id || Number(it.cantidad) <= 0)) return toast.error("Revisa los productos y cantidades");
    if (tipoVenta === "rtc" && !vendedorRtcId) return toast.error("Selecciona el RTC asociado a esta venta");
    if (imprimirGuia && !destinoEnvioId && !nuevoDestino) return toast.error("Selecciona o crea un destino para la guía de remisión");
    setSaving(true);
    try {
      const payload = {
        tipo, forma_pago: formaPago, cliente_id: clienteId,
        vendedor, vendedor_rtc_id: vendedorRtcId || null,
        region, tipo_venta: tipoVenta,
        cuenta_cxc_codigo: cuentaCxc || "1103",
        codigo_promocional: codigoPromocional,
        numero_orden_compra_exenta: ocExenta,
        numero_constancia_exonerado: constanciaExo,
        numero_registro_sag: registroSag,
        imprimir_guia: imprimirGuia,
        destino_envio_id: destinoEnvioId || null,
        destino_envio_nuevo: imprimirGuia && !destinoEnvioId && nuevoDestino ? nuevoDestino : null,
        enviar_correo: enviarCorreo,
        proyecto_id: proyectoId || null,
        dependiente_id: dependienteId || null,
        fecha_vencimiento: formaPago === "credito" && fechaVenc ? new Date(fechaVenc).toISOString() : null,
        plazo_credito_dias: formaPago === "credito" && plazo ? Number(plazo) : null,
        items: items.map((it) => ({
          producto_id: it.producto_id,
          codigo: it.codigo,
          descripcion: it.descripcion,
          cantidad: Number(it.cantidad), precio_unitario: Number(it.precio_unitario),
          descuento: Number(it.descuento) || 0, isv_pct: Number(it.isv_pct),
          nivel_aplicado: it.p0_manual ? "p0" : (it.nivel_aplicado || ""),
          nota_precio: (it.nota_precio || "").trim(),
          entry_id_manual: it.lote_manual ? (it.entry_id || null) : null,
        })),
        notas,
        vigencia_oferta: vigencia || null,
        condiciones_comerciales: condiciones,
      };
      const sinNota = items.filter((it) => requiereNota(it) && !(it.nota_precio || "").trim());
      if (sinNota.length) {
        toast.error(`Escribe la nota del cambio de precio en: ${sinNota.map((it) => it.codigo).join(", ")}`);
        return;
      }
      if (corregir) {
        if (motivoCorreccion.trim().length < 5) { toast.error("Indica el motivo de la corrección"); return; }
        if (editando && clienteId !== editando.cliente_id) { toast.error("La corrección conserva el cliente de la factura"); return; }
        if (!window.confirm(`¿Está seguro de que desea guardar la corrección de la factura ${editando?.numero || ""}? Conserva su número; se revierten y vuelven a aplicar inventario, contabilidad y caja.`)) return;
        await api.post(`/invoices/${editId}/corregir`, {
          motivo: motivoCorreccion, forma_pago: formaPago, plazo_credito_dias: payload.plazo_credito_dias,
          items: payload.items, notas,
        });
        toast.success(`Factura ${editando?.numero || ""} corregida (mismo número). La versión anterior quedó en el historial.`, { duration: 8000 });
        navigate("/ventas");
        return;
      }
      if (editId) {
        await api.put(`/sales/proformas/${editId}`, payload);
        toast.success(puedeTarifa
          ? `Proforma ${editando?.numero || ""} actualizada${items.some((it) => it.p0_manual || (it.nivel_aplicado || "") !== (it.nivel_lista || "")) ? " · cambio de tarifa registrado en la bitácora" : ""}.`
          : `Proforma ${editando?.numero || ""} actualizada. Envíala a aprobación desde la lista.`);
        navigate("/ventas");
        return;
      }
      const { data } = await api.post("/invoices", payload);
      toast.success(`Proforma ${data.numero} creada`);
      // Si solicitó imprimir guía, abrirla en nueva pestaña (se generará formalmente al convertir a factura)
      if (imprimirGuia && data.id) {
        window.open(`/ventas/${data.id}/guia`, "_blank");
      }
      navigate("/ventas");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  // Flechas del ERP: la proforma solo se crea con su propio botón (no desde el aviso de navegación)
  const hayDatos = useCallback(() => !saving && (items.length > 0 || Boolean(clienteId)), [saving, items, clienteId]);
  useNavGuard("invoice-new", {
    isDirty: hayDatos, sinGuardar: "La proforma se crea con el botón “Guardar proforma” después de revisarla; descarta o cancela.",
  });

  return (
    <Layout
      title={corregir ? `Corregir factura ${editando?.numero || ""}` : editId ? `Editar proforma ${editando?.numero || ""}` : "Nueva proforma"}
      subtitle={corregir ? "La factura conserva su número, CAI y fecha de emisión. Solo Administrador General."
        : "Toda venta inicia con proforma. Después se convierte a factura formal con CAI desde la lista."}
      actions={
        <div className="flex gap-2">
          <button onClick={() => navigate("/ventas")} className="px-3 py-2 text-sm border border-border rounded-md text-text-secondary hover:text-text-primary flex items-center gap-2">
            <ArrowLeftIcon size={14} /> Volver
          </button>
          <button onClick={save} disabled={saving} data-testid="save-invoice-btn"
            className="bg-agro-green hover:bg-agro-green-hover text-black font-medium rounded-md px-5 py-2 text-sm transition-all active:scale-95 disabled:opacity-50">
            {saving ? "Guardando…" : corregir ? "Guardar corrección" : "Guardar proforma"}
          </button>
        </div>
      }
    >
      {corregir && (
        <div className="mb-4 px-4 py-3 rounded-md text-xs border border-agro-yellow/40 bg-agro-yellow/5" data-testid="corregir-aviso">
          <div className="text-agro-yellow font-semibold uppercase tracking-wider text-[10px]">Corrección de factura emitida · conserva el número</div>
          <div className="text-text-secondary mt-1">
            Ajusta tarifas, precios, cantidades, productos, forma de pago o plazo. Al guardar se revierten los efectos de la versión
            anterior (inventario, asientos contables y caja) y se aplican los de la corregida. La versión anterior completa queda en el
            historial de la factura con el motivo, el usuario y la fecha. Solo procede si la factura no tiene cobros ni notas vigentes.
          </div>
          <label className="block text-[10px] uppercase tracking-wider text-text-muted mt-2 mb-1">Motivo de la corrección *</label>
          <input value={motivoCorreccion} onChange={(e) => setMotivoCorreccion(e.target.value)} data-testid="corregir-motivo"
            placeholder="Ej.: tarifa incorrecta, cantidad equivocada…" className="w-full bg-app border border-border rounded px-3 py-2 text-sm" />
        </div>
      )}
      {prefill && (
        <div className="mb-4 px-4 py-2.5 rounded-md text-xs border border-fuchsia-500/40 bg-fuchsia-500/10 text-fuchsia-200" data-testid="prefill-aviso">
          <strong>PROFORMA PRECARGADA DESDE UNA OPORTUNIDAD SUGERIDA.</strong> Las cantidades son estimaciones con ventas anteriores y los precios son los
          VIGENTES del cliente. Revise y ajuste productos, cantidades y precios antes de guardar. No se envía nada al cliente.
          {items.some((it) => it.producto_id && !Number(it.precio_unitario)) && (
            <div className="mt-1 text-orange-300" data-testid="prefill-sin-precio">
              {items.filter((it) => it.producto_id && !Number(it.precio_unitario)).length} producto(s) no tienen precio vigente en la lista de precios:
              capture el precio de venta antes de guardar (el precio histórico de referencia está en NOTAS).
            </div>
          )}
        </div>
      )}
      {clienteId && (
        <div className={`mb-4 px-4 py-2.5 rounded-md text-xs border ${
          canOverridePrice
            ? "border-agro-green/30 bg-agro-green/5 text-agro-green"
            : "border-agro-yellow/30 bg-agro-yellow/5 text-agro-yellow"
        }`} data-testid="price-permission-banner">
          {canOverridePrice ? (
            <><LockOpenIcon size={12} className="inline align-[-2px]" aria-hidden="true" /> Precios <strong>editables</strong>. Cliente con nivel <strong>{(nivelCliente || "—").toUpperCase()}</strong>. Puedes sobrescribir el precio por línea o capturar precio <strong>P0</strong> (negocio especial).</>
          ) : (
            <><LockKeyIcon size={12} className="inline align-[-2px]" aria-hidden="true" /> Precios <strong>bloqueados</strong> al nivel del cliente (<strong>{(nivelCliente || "—").toUpperCase()}</strong>, o el nivel asignado por producto). Solicita permiso <code>sales.override_price</code> al administrador para modificar.</>
          )}
        </div>
      )}

      {creditAlert && (
        <div className={`mb-4 px-4 py-2.5 rounded-md text-xs border flex items-center gap-2 ${creditAlert.excede ? "border-red-500/40 bg-red-500/10 text-red-300" : "border-orange-500/40 bg-orange-500/10 text-orange-300"}`}
             data-testid="credit-limit-alert">
          <WarningIcon size={16} weight="fill" />
          {creditAlert.excede
            ? <>Con esta venta el cliente <strong>supera su límite de crédito</strong>: {fmtL(creditAlert.proyectado)} de {fmtL(balance.limite_credito)} ({creditAlert.pct}%). Requiere autorización temporal o permiso de override.</>
            : <>El cliente está cerca de su límite de crédito: {fmtL(creditAlert.proyectado)} de {fmtL(balance.limite_credito)} ({creditAlert.pct}%).</>}
          {balance?.saldo_vencido > 0 && <span className="ml-2">· Vencido: {fmtL(balance.saldo_vencido)}</span>}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          {/* Datos generales */}
          <div className="bg-card border border-border/60 rounded-lg p-5">
            <h3 className="font-heading text-sm uppercase tracking-wider text-text-muted mb-4">Datos generales</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Lbl>Forma de pago</Lbl>
                <select data-testid="invoice-forma-pago" value={formaPago} onChange={(e) => setFormaPago(e.target.value)} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm">
                  <option value="contado">Contado</option>
                  <option value="credito">Crédito</option>
                </select>
              </div>
              <div>
                <Lbl>Tipo documento</Lbl>
                <div className="px-3 py-2 text-sm bg-app/40 border border-border rounded-md text-agro-yellow flex items-center gap-2" data-testid="tipo-locked">
                  <span className="text-[10px] uppercase tracking-wider bg-agro-yellow/20 text-agro-yellow border border-agro-yellow/30 rounded px-2 py-0.5">{corregir ? "Factura" : "Proforma"}</span>
                  <span className="text-[10px] text-text-muted">{corregir ? `Corrección de ${editando?.numero || ""} (mismo número)` : "Se convertirá a factura al aprobar"}</span>
                </div>
              </div>
              <div className="col-span-2">
                <Lbl>Cliente *</Lbl>
                <ClientSearch clients={clients} value={clienteId} onPick={onClientChange} testid="invoice-cliente" />
                {cliente && (
                  <div className="mt-2 flex flex-wrap gap-2 items-center text-xs">
                    <Chip label="RTN" value={cliente.rtn || "—"} />
                    <Chip label="Región" value={cliente.region || "—"} accent />
                    <Chip label="Crédito" value={`${fmtL(cliente.limite_credito)} · ${cliente.dias_credito}d`} />
                    <Chip label="Nivel precio" value={(nivelCliente || "—").toUpperCase()} />
                    {balance && balance.limite_credito > 0 && (
                      <Chip label="Crédito usado" value={`${balance.porcentaje_uso_credito}%`} warning={balance.porcentaje_uso_credito >= 80} />
                    )}
                    {cliente.exonerado && <Chip label="Estatus" value="EXONERADO" warning />}
                    {cliente.rtc_asignado_nombre && <Chip label="RTC asignado" value={cliente.rtc_asignado_nombre} accent />}
                  </div>
                )}
              </div>
              {/* Cuenta CxC (PRD 5.2) — solo si crédito */}
              {formaPago === "credito" && (
                <div>
                  <Lbl>Cuenta por cobrar (CxC)</Lbl>
                  <select data-testid="invoice-cuenta-cxc" value={cuentaCxc} onChange={(e) => setCuentaCxc(e.target.value)} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm">
                    <option value="1103">1103 - CxC Comerciales</option>
                    {accounts.filter((a) => a.codigo !== "1103").map((a) => (
                      <option key={a.codigo} value={a.codigo}>{a.codigo} - {a.nombre}</option>
                    ))}
                  </select>
                </div>
              )}
              {formaPago === "credito" && (
                <div>
                  <Lbl>Plazo de crédito *</Lbl>
                  <select data-testid="invoice-plazo" value={plazo} onChange={(e) => elegirPlazo(e.target.value)}
                    className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm">
                    <option value="">— Selecciona el plazo —</option>
                    {opcionesPlazo.map((d) => {
                      const excede = diasAutorizados > 0 && d > diasAutorizados;
                      return (
                        <option key={d} value={d} disabled={excede && !puedePlazo}>
                          {d} días{d === diasAutorizados ? " (autorizado al cliente)" : ""}{excede ? (puedePlazo ? " · excede lo autorizado" : " · requiere Delegado") : ""}
                        </option>
                      );
                    })}
                  </select>
                  <div className="text-[10px] text-text-muted mt-1">
                    Vence el <strong>{fechaVenc || "—"}</strong> (al facturar se cuenta desde la fecha de la factura).
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Tipo de venta (PRD 5.3) — RTC y vendedor se autoseleccionan del cliente */}
          <div className="bg-card border border-border/60 rounded-lg p-5">
            <h3 className="font-heading text-sm uppercase tracking-wider text-text-muted mb-1">Tipo de venta</h3>
            <p className="text-[11px] text-text-muted mb-4">El RTC y la región se asignan automáticamente desde el cliente. Cambia solo si esta venta es una excepción.</p>
            <div className="grid grid-cols-2 gap-2 mb-4">
              <label className={`flex items-center gap-3 p-3 border rounded-md cursor-pointer ${tipoVenta === "directa" ? "border-agro-green bg-agro-green/5" : "border-border"}`}>
                <input type="radio" data-testid="tipo-venta-directa" name="tipo_venta" value="directa" checked={tipoVenta === "directa"} onChange={() => { setTipoVenta("directa"); setVendedorRtcId(""); setVendedor(user?.name || ""); }} className="accent-agro-green" />
                <div>
                  <div className="font-medium text-sm">Venta directa</div>
                  <div className="text-[10px] text-text-muted">Sin GRATIVA RTC</div>
                </div>
              </label>
              <label className={`flex items-center gap-3 p-3 border rounded-md cursor-pointer ${tipoVenta === "rtc" ? "border-agro-yellow bg-agro-yellow/5" : "border-border"}`}>
                <input type="radio" data-testid="tipo-venta-rtc" name="tipo_venta" value="rtc" checked={tipoVenta === "rtc"} onChange={() => setTipoVenta("rtc")} className="accent-agro-yellow" />
                <div>
                  <div className="font-medium text-sm">Venta por RTC</div>
                  <div className="text-[10px] text-text-muted">Genera GRATIVA (Gratificación Variable)</div>
                </div>
              </label>
            </div>
            {tipoVenta === "rtc" ? (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Lbl>RTC *</Lbl>
                  <select
                    data-testid="invoice-rtc"
                    value={vendedorRtcId}
                    onChange={(e) => { const r = reps.find((x) => x.id === e.target.value); setVendedorRtcId(e.target.value); if (r) setVendedor(r.nombre); }}
                    className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm"
                  >
                    <option value="">— Selecciona RTC —</option>
                    {reps.filter((r) => r.activo !== false).map((r) => (
                      <option key={r.id} value={r.id}>{r.codigo} - {r.nombre}{r.region ? ` · ${r.region}` : ""}</option>
                    ))}
                  </select>
                  {cliente?.rtc_asignado_id && vendedorRtcId !== cliente.rtc_asignado_id && (
                    <div className="text-[10px] text-agro-yellow mt-1"><WarningIcon size={11} className="inline align-[-2px]" aria-hidden="true" /> RTC asignado al cliente: <strong>{cliente.rtc_asignado_nombre}</strong></div>
                  )}
                </div>
                <div>
                  <Lbl>Región</Lbl>
                  <div className="px-3 py-2 text-sm bg-app/40 border border-border rounded-md text-text-secondary" data-testid="region-derived">
                    {region || <span className="text-text-muted italic">— sin región en cliente —</span>}
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Lbl>Vendedor</Lbl>
                  <div className="px-3 py-2 text-sm bg-app/40 border border-border rounded-md text-text-secondary" data-testid="vendedor-display">
                    {vendedor || <span className="text-text-muted italic">— sin vendedor —</span>}
                  </div>
                  <div className="text-[10px] text-text-muted mt-1">Tomado del cliente o del usuario actual.</div>
                </div>
                <div>
                  <Lbl>Región</Lbl>
                  <div className="px-3 py-2 text-sm bg-app/40 border border-border rounded-md text-text-secondary" data-testid="region-derived">
                    {region || <span className="text-text-muted italic">— sin región en cliente —</span>}
                  </div>
                </div>
              </div>
            )}
            {/* VEN-01 — Dependiente de tienda (aplica a venta directa y RTC) */}
            <div className="mt-3 pt-3 border-t border-border/60">
              <Lbl>Dependiente de tienda (opcional)</Lbl>
              <select value={dependienteId} onChange={(e) => setDependienteId(e.target.value)}
                data-testid="invoice-dependiente"
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm">
                <option value="">— Sin dependiente —</option>
                {empleados.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.codigo} · {emp.nombre} {emp.puesto && `(${emp.puesto})`}
                  </option>
                ))}
              </select>
              <div className="text-[10px] text-text-muted mt-1">Operador en mostrador. En algunos casos participa del Cálculo GRATIVA (COM-01).</div>
            </div>
          </div>

          {/* Líneas de producto */}
          <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
            <div className="p-5 border-b border-border/60 flex items-center justify-between">
              <h3 className="font-heading text-sm uppercase tracking-wider text-text-muted">Líneas de producto</h3>
              <button onClick={addRow} data-testid="add-item-btn" className="text-xs bg-agro-green/10 text-agro-green border border-agro-green/30 rounded-md px-3 py-1.5 flex items-center gap-1.5 hover:bg-agro-green/20">
                <PlusIcon size={12} weight="bold" /> Agregar línea
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-text-muted text-left">
                    <th className="p-2 pl-5">Producto / Descripción</th>
                    <th className="p-2 text-right w-20">Cant.</th>
                    <th className="p-2 text-right w-28">Precio</th>
                    <th className="p-2 text-right w-20">Desc.</th>
                    <th className="p-2 w-20">ISV</th>
                    <th className="p-2 text-right w-28">Subtotal</th>
                    <th className="p-2 text-right w-28">Total</th>
                    <th className="p-2 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, i) => {
                    const bruto = (Number(it.cantidad) || 0) * (Number(it.precio_unitario) || 0);
                    const neto = bruto - (Number(it.descuento) || 0);
                    const linea = exonerado ? neto : neto * (1 + Number(it.isv_pct) / 100);
                    return (
                      <tr key={i} className="border-t border-border/40">
                        <td className="p-2 pl-5">
                          <ProductSearch
                            products={products}
                            value={it.producto_id}
                            onPick={(pid) => pickProduct(i, pid)}
                            testid={`row-product-${i}`}
                          />
                          {it.producto_id && (
                            <input value={it.descripcion || ""} onChange={(e) => updateRow(i, { descripcion: e.target.value })}
                              data-testid={`row-desc-${i}`} placeholder="Descripción en la factura"
                              className="mt-1 w-full bg-app border border-border rounded px-2 py-1 text-xs" />
                          )}
                          {(it.fecha_ingreso || it.lote || it.peps_breakdown) && (
                            <div className="mt-1 text-[10px] leading-tight" data-testid={`row-lote-${i}`}>
                              {it.peps_breakdown && it.peps_breakdown.lotes && it.peps_breakdown.lotes.length >= 1 && !it.lote_manual ? (
                                <>
                                  <div className="flex items-center gap-1 flex-wrap">
                                    <span className="text-agro-yellow font-semibold">
                                      <PackageIcon size={11} className="inline align-[-2px]" aria-hidden="true" /> PEPS {it.peps_breakdown.lotes.length > 1 ? `· ${it.peps_breakdown.lotes.length} lotes` : ""}:
                                    </span>
                                    {it.peps_breakdown.lotes.map((l, k) => (
                                      <span key={k} className="inline-flex items-center gap-0.5 border border-border/60 rounded px-1.5 py-0.5 bg-app">
                                        <span className="font-mono text-agro-green">{l.lote}</span>
                                        <span className="text-text-muted">×</span>
                                        <span className="text-text-primary font-semibold">{l.cantidad_tomada}</span>
                                        <span className="text-text-muted text-[9px] ml-0.5">({l.fecha_ingreso})</span>
                                      </span>
                                    ))}
                                    {it.lotes_disponibles && it.lotes_disponibles.length > 1 && (
                                      <button type="button" onClick={() => toggleLotPicker(i)}
                                              data-testid={`row-change-lot-${i}`}
                                              className="ml-1 text-[10px] underline text-agro-yellow hover:text-agro-green">
                                        elegir lote manual ({it.lotes_disponibles.length})
                                      </button>
                                    )}
                                  </div>
                                  {!it.peps_breakdown.suficiente && (
                                    <div className="mt-0.5 text-red-400"><WarningIcon size={11} className="inline align-[-2px]" aria-hidden="true" /> Faltan {it.peps_breakdown.sin_stock_por} — solo hay {it.peps_breakdown.total_disponible} en stock</div>
                                  )}
                                </>
                              ) : (
                                <div className="flex items-center gap-1.5" title={it.lote_manual ? "Lote elegido manualmente" : "Lote más viejo con saldo (PEPS)"}>
                                  <span className={it.lote_manual ? "text-agro-green font-semibold" : "text-agro-yellow"}>
                                    {it.lote_manual ? <><HandIcon size={11} className="inline align-[-2px]" aria-hidden="true" /> Manual:</> : <><PackageIcon size={11} className="inline align-[-2px]" aria-hidden="true" /> PEPS:</>}
                                  </span>
                                  {it.lote && <span className="font-mono text-agro-green">{it.lote}</span>}
                                  {it.fecha_ingreso && <span className="text-text-secondary">· ing {it.fecha_ingreso}</span>}
                                  {typeof it.cantidad_disponible === "number" && it.cantidad_disponible > 0 && (
                                    <span className="text-text-muted">· disp {it.cantidad_disponible}</span>
                                  )}
                                  {it.lotes_disponibles && it.lotes_disponibles.length > 1 && (
                                    <button type="button" onClick={() => toggleLotPicker(i)}
                                            data-testid={`row-change-lot-${i}`}
                                            className="ml-1 text-[10px] underline text-agro-yellow hover:text-agro-green">
                                      cambiar lote ({it.lotes_disponibles.length})
                                    </button>
                                  )}
                                </div>
                              )}
                              {it.show_lot_picker && it.lotes_disponibles && (
                                <div className="mt-1.5 border border-border/60 rounded-md bg-card p-2 max-h-56 overflow-y-auto" data-testid={`row-lot-picker-${i}`}>
                                  <div className="text-[10px] uppercase tracking-wider text-text-muted mb-1.5">Elige un lote:</div>
                                  {it.lotes_disponibles.map((l) => {
                                    const isSel = it.entry_id === l.entry_id;
                                    return (
                                      <button type="button" key={l.entry_id}
                                              onClick={() => chooseLot(i, l)}
                                              data-testid={`row-lot-option-${l.entry_id}`}
                                              className={`w-full text-left px-2 py-1 rounded text-[11px] mb-0.5 hover:bg-white/[0.03] flex items-center gap-2 ${isSel ? "bg-agro-green/15 border border-agro-green/40" : "border border-transparent"}`}>
                                        <span className={`font-mono ${isSel ? "text-agro-green font-semibold" : "text-agro-green"}`}>{l.lote || "(sin lote)"}</span>
                                        <span className="text-text-muted">·</span>
                                        <span className="text-text-secondary">ing {l.fecha_ingreso || "—"}</span>
                                        {l.fecha_vencimiento && <span className="text-text-muted">· vence {l.fecha_vencimiento}</span>}
                                        <span className="ml-auto text-agro-yellow font-semibold">{l.cantidad_restante}</span>
                                        <span className="text-text-muted">/{l.cantidad_inicial}</span>
                                      </button>
                                    );
                                  })}
                                  <div className="pt-1 mt-1 border-t border-border/40 text-right">
                                    <button type="button" onClick={() => toggleLotPicker(i)}
                                            className="text-[10px] text-text-muted hover:text-agro-yellow">
                                      cerrar
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="p-2"><input type="number" data-testid={`row-qty-${i}`} value={it.cantidad} onChange={(e) => changeQuantity(i, e.target.value)} className="w-full bg-app border border-border rounded px-2 py-1.5 text-xs text-right" /></td>
                        <td className="p-2">
                          <div className="relative">
                            <input type="number"
                              data-testid={`row-price-${i}`}
                              value={it.precio_unitario}
                              readOnly={!canOverridePrice}
                              onChange={(e) => cambiarPrecio(i, e.target.value)}
                              title={canOverridePrice ? `Nivel aplicado: ${(it.nivel_aplicado || "").toUpperCase()}` : "El precio lo cambian el Administrador General o el Delegado"}
                              className={`w-full bg-app border border-border rounded px-2 py-1.5 text-xs text-right ${
                                !canOverridePrice ? "cursor-not-allowed opacity-80" : ""
                              }`} />
                            {!canOverridePrice && (
                              <LockIcon size={10} className="absolute right-1.5 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none" />
                            )}
                          </div>
                          {it.producto_id && (
                            <div className="mt-0.5 flex items-center justify-end gap-1 text-[9px] uppercase">
                              {puedeTarifa && !it.p0_manual ? (
                                <select value={(it.nivel_aplicado || "").toLowerCase()} onChange={(e) => cambiarTarifa(i, e.target.value)}
                                  data-testid={`row-tarifa-${i}`} title="Tarifa de precio de la línea (queda en la bitácora)"
                                  className={`bg-app border rounded px-1 py-0 text-[9px] uppercase ${(it.nivel_aplicado || "") !== (it.nivel_lista || "") ? "border-agro-yellow text-agro-yellow" : "border-border text-text-muted"}`}>
                                  {["p1", "p2", "p3", "p4"].map((n) => <option key={n} value={n}>{n.toUpperCase()}</option>)}
                                </select>
                              ) : (
                                <span className={it.p0_manual ? "text-agro-yellow font-semibold" : "text-text-muted"} data-testid={`row-nivel-${i}`}>
                                  {(it.p0_manual ? "p0" : it.nivel_aplicado || "").toUpperCase()}
                                  {!it.p0_manual && Number(it.precio_lista) > 0 && Math.abs(Number(it.precio_unitario) - Number(it.precio_lista)) > 0.009 && " · mod."}
                                </span>
                              )}
                              {canOverridePrice && (
                                <button type="button" onClick={() => toggleP0(i)} data-testid={`row-p0-${i}`}
                                  title="Capturar precio P0 (negocio especial)"
                                  className={`px-1 rounded border ${it.p0_manual ? "border-agro-yellow text-agro-yellow" : "border-border text-text-muted hover:text-agro-yellow"}`}>P0</button>
                              )}
                            </div>
                          )}
                          {it.producto_id && canOverridePrice && lineaCambiada(it) && (
                            <input value={it.nota_precio || ""} onChange={(e) => updateRow(i, { nota_precio: e.target.value })}
                              data-testid={`row-nota-${i}`} maxLength={300}
                              placeholder={requiereNota(it) ? (it.p0_manual ? "Nota del P0 (obligatoria)" : "Nota del cambio (obligatoria)") : "Nota del cambio (opcional)"}
                              className={`mt-1 w-full bg-app border rounded px-2 py-1 text-[10px] ${requiereNota(it) && !(it.nota_precio || "").trim() ? "border-red-500/70" : "border-border"}`} />
                          )}
                        </td>
                        <td className="p-2"><input type="number" value={it.descuento} onChange={(e) => updateRow(i, { descuento: e.target.value })} className="w-full bg-app border border-border rounded px-2 py-1.5 text-xs text-right" /></td>
                        <td className="p-2"><select value={it.isv_pct} onChange={(e) => updateRow(i, { isv_pct: e.target.value })} className="w-full bg-app border border-border rounded px-1 py-1.5 text-xs">
                          <option value={0}>0%</option><option value={15}>15%</option><option value={18}>18%</option>
                        </select></td>
                        <td className="p-2 text-right text-xs text-text-secondary" data-testid={`row-subtotal-${i}`}>{fmtL(neto)}</td>
                        <td className="p-2 text-right text-xs font-medium text-agro-green">{fmtL(linea)}</td>
                        <td className="p-2"><button aria-label="Eliminar" onClick={() => removeRow(i)} className="text-text-muted hover:text-red-400"><TrashIcon size={14} /></button></td>
                      </tr>
                    );
                  })}
                  {items.length === 0 && <tr><td colSpan="8" className="p-8 text-center text-text-muted text-sm">Agrega productos a la factura</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          {/* Datos fiscales extra (PRD 5.2) — se conservan al convertir a factura */}
          <details className="bg-card border border-border/60 rounded-lg p-5" data-testid="datos-fiscales-extra">
            <summary className="font-heading text-sm uppercase tracking-wider text-text-muted cursor-pointer">
              Datos fiscales adicionales (opcional)
            </summary>
            <div className="grid grid-cols-3 gap-4 mt-4">
              <div>
                <Lbl>N° orden compra exenta</Lbl>
                <input data-testid="oc-exenta" value={ocExenta} onChange={(e) => setOcExenta(e.target.value)} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm" placeholder="—" />
              </div>
              <div>
                <Lbl>N° constancia exonerado</Lbl>
                <input data-testid="constancia-exo" value={constanciaExo} onChange={(e) => setConstanciaExo(e.target.value)} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm" placeholder="—" />
              </div>
              <div>
                <Lbl>N° registro SAG</Lbl>
                <input data-testid="registro-sag" value={registroSag} onChange={(e) => setRegistroSag(e.target.value)} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm" placeholder="—" />
              </div>
            </div>
          </details>

          {/* Guía de remisión (PRD 5.4 / 2.5) — Sí/No explícito; destino existente o nuevo */}
          <div className="bg-card border border-border/60 rounded-lg p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-sm uppercase tracking-wider text-text-muted flex items-center gap-2">
                <TruckIcon size={16} /> Imprimir guía de remisión
              </h3>
              <div className="flex gap-2" data-testid="imprimir-guia">
                {[["si", "Sí"], ["no", "No"]].map(([v, l]) => (
                  <button key={v} type="button" data-testid={`imprimir-guia-${v}`}
                    onClick={() => setImprimirGuia(v === "si")}
                    className={`px-4 py-1.5 text-xs rounded-md border ${(imprimirGuia ? "si" : "no") === v ? "border-agro-green bg-agro-green/10 text-agro-green" : "border-border text-text-secondary"}`}>{l}</button>
                ))}
              </div>
            </div>
            {imprimirGuia && (
              <div className="space-y-2">
                <Lbl>Destino *</Lbl>
                <select data-testid="invoice-destino" value={destinoEnvioId}
                  onChange={(e) => { setDestinoEnvioId(e.target.value); if (e.target.value) setNuevoDestino(null); }}
                  className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm">
                  <option value="">{nuevoDestino ? `Nuevo: ${nuevoDestino.alias} · ${nuevoDestino.direccion}` : "— Selecciona destino —"}</option>
                  {destinosCliente.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.alias} {d.encargado && `· ${d.encargado}`} {d.ciudad && `· ${d.ciudad}`}
                    </option>
                  ))}
                </select>
                {!nuevoDestinoOpen ? (
                  <button type="button" onClick={() => setNuevoDestinoOpen(true)} disabled={!clienteId} data-testid="nuevo-destino-btn"
                    className="text-xs text-agro-green hover:underline disabled:opacity-40 flex items-center gap-1">
                    <PlusIcon size={12} /> Crear destino nuevo
                  </button>
                ) : (
                  <NewDestinationForm
                    savesToClient={destinosCliente.length < 5}
                    onCancel={() => setNuevoDestinoOpen(false)}
                    onSave={(d) => {
                      setNuevoDestino({ ...d, guardar_en_cliente: destinosCliente.length < 5 });
                      setDestinoEnvioId("");
                      setNuevoDestinoOpen(false);
                    }} />
                )}
              </div>
            )}
          </div>

          {/* 2.1 — Notificación por correo al emitir la factura */}
          {clienteId && (
            <div className="bg-card border border-border/60 rounded-lg p-5 flex items-center justify-between gap-3">
              <div>
                <Lbl>Enviar factura por correo al emitir</Lbl>
                <div className="text-[11px] text-text-muted">
                  {cliente?.email ? `${cliente.email} · preferencia del cliente: ${cliente.notificar_correo === false ? "no notificar" : "notificar"}` : "El cliente no tiene correo registrado"}
                </div>
              </div>
              <div className="flex gap-2">
                {[["si", "Sí"], ["no", "No"]].map(([v, l]) => (
                  <button key={v} type="button" data-testid={`enviar-correo-${v}`} disabled={v === "si" && !cliente?.email}
                    onClick={() => setEnviarCorreo(v === "si")}
                    className={`px-4 py-1.5 text-xs rounded-md border disabled:opacity-40 ${(enviarCorreo ? "si" : "no") === v ? "border-agro-green bg-agro-green/10 text-agro-green" : "border-border text-text-secondary"}`}>{l}</button>
                ))}
              </div>
            </div>
          )}

          {/* CLI-01 — Subcuenta/Proyecto del cliente (opcional) */}
          {clienteId && (() => {
            // Proyectos "reales" (registrados). Los virtuales (derivados de facturas
            // sin proyecto creado) se muestran informativamente pero no se pueden
            // seleccionar para una proforma nueva porque no tienen un ID válido en DB.
            // subcuentas registradas (incluidas las precargadas del estado de cuenta), ordenadas por código
            const realProjects = proyectosCliente.filter((p) => !p.virtual && (p.estado || "activo") === "activo")
              .sort((a, b) => String(a.codigo_subcuenta || a.codigo).localeCompare(String(b.codigo_subcuenta || b.codigo)));
            const virtualProjects = proyectosCliente.filter((p) => p.virtual);
            // Si el cliente no posee proyectos, el campo permanece oculto
            if (realProjects.length === 0 && virtualProjects.length === 0) return null;
            return (
              <div className="bg-card border border-border/60 rounded-lg p-5">
                <Lbl>Subcuenta / proyecto del cliente</Lbl>
                {realProjects.length === 0 && virtualProjects.length === 0 ? (
                  <div className="text-[11px] text-text-muted p-2 border border-dashed border-border rounded-md">
                    Este cliente no tiene proyectos. Sin asignar → la factura va a la cuenta general.
                  </div>
                ) : (
                  <>
                    <select data-testid="invoice-proyecto" value={proyectoId} onChange={(e) => setProyectoId(e.target.value)} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm">
                      <option value="">— Cuenta general (sin proyecto) —</option>
                      {realProjects.map((p) => (
                        <option key={p.id} value={p.id}>{p.codigo_subcuenta ? `${p.codigo_subcuenta} · ` : ""}{p.nombre}{p.codigo && p.codigo !== p.nombre && p.codigo !== p.codigo_subcuenta ? ` (${p.codigo})` : ""}</option>
                      ))}
                    </select>
                    {virtualProjects.length > 0 && (
                      <div className="mt-2 text-[10px] text-text-muted border-l-2 border-agro-yellow/40 pl-2">
                        <span className="text-agro-yellow font-medium">Proyectos detectados en facturas anteriores</span> (no seleccionables aquí — créalos formalmente en la pestaña Proyectos del cliente para usarlos):{" "}
                        {virtualProjects.map((p) => p.codigo).join(", ")}
                      </div>
                    )}
                  </>
                )}
                <div className="text-[10px] text-text-muted mt-1">Esta factura figurará individualmente bajo el proyecto elegido en el estado de cuenta del cliente.</div>
              </div>
            );
          })()}

          {/* Código promocional (PRD 5.1) — se conserva al convertir a factura */}
          <div className="bg-card border border-border/60 rounded-lg p-5">
            <Lbl>Código promocional</Lbl>
            <input data-testid="codigo-promocional" value={codigoPromocional} onChange={(e) => setCodigoPromocional(e.target.value)} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm" placeholder="P. ej. AGRO-2026" />
            <div className="text-[10px] text-text-muted mt-1">El código se conserva al convertir la proforma a factura.</div>
          </div>

          <div className="bg-card border border-border/60 rounded-lg p-5 grid md:grid-cols-3 gap-4" data-testid="proforma-condiciones">
            <div>
              <Lbl>Válida hasta</Lbl>
              <input type="date" value={vigencia} onChange={(e) => setVigencia(e.target.value)} data-testid="proforma-vigencia"
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm" />
              <div className="text-[10px] text-text-muted mt-1">Vigencia de la oferta impresa en la proforma.</div>
            </div>
            <div className="md:col-span-2">
              <Lbl>Condiciones comerciales</Lbl>
              <textarea value={condiciones} onChange={(e) => setCondiciones(e.target.value)} rows={2} data-testid="proforma-condiciones-txt"
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm" placeholder="Condiciones de la oferta…" />
            </div>
          </div>

          <div className="bg-card border border-border/60 rounded-lg p-5">
            <Lbl>Notas</Lbl>
            <textarea value={notas} onChange={(e) => setNotas(e.target.value)} rows={2} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm" placeholder="Notas u observaciones…" />
          </div>
        </div>

        {/* Resumen */}
        <div className="space-y-5">
          <div className="bg-card border border-border/60 rounded-lg p-5 sticky top-20">
            <h3 className="font-heading text-sm uppercase tracking-wider text-text-muted mb-4">Resumen</h3>
            <div className="space-y-2 text-sm">
              <TR l="Subtotal" v={fmtL(totals.subtotal)} />
              <TR l="Gravado 15%" v={fmtL(totals.s15)} />
              <TR l="Gravado 18%" v={fmtL(totals.s18)} />
              <TR l="Exento" v={fmtL(totals.sx)} />
              <TR l="Exonerado" v={fmtL(totals.sxo)} />
              <TR l="Descuento total" v={fmtL(totals.td)} />
              <div className="h-px bg-border my-2" />
              <TR l="ISV 15%" v={fmtL(totals.i15)} />
              <TR l="ISV 18%" v={fmtL(totals.i18)} />
              <div className="h-px bg-border my-2" />
              <div className="flex justify-between items-baseline pt-2">
                <span className="text-text-secondary text-xs uppercase tracking-wider">Total a pagar</span>
                <span className="font-heading text-3xl font-semibold text-agro-green" data-testid="invoice-total">{fmtL(totals.total)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

function NewDestinationForm({ savesToClient, onSave, onCancel }) {
  const [d, setD] = useState({ alias: "", direccion: "", encargado: "", telefono: "", ciudad: "", departamento: "", referencia: "" });
  const f = (k, label, cls = "") => (
    <input value={d[k]} onChange={(e) => setD({ ...d, [k]: e.target.value })} placeholder={label}
      data-testid={`nuevo-destino-${k}`} className={`bg-app border border-border rounded-md px-2 py-1.5 text-xs ${cls}`} />
  );
  return (
    <div className="border border-agro-green/30 rounded-md p-3 space-y-2" data-testid="nuevo-destino-form">
      <div className="grid grid-cols-2 gap-2">
        {f("alias", "Alias (p.ej. Finca norte) *")}
        {f("encargado", "Encargado")}
        {f("direccion", "Dirección *", "col-span-2")}
        {f("ciudad", "Ciudad")}
        {f("departamento", "Departamento")}
        {f("telefono", "Teléfono")}
        {f("referencia", "Referencia")}
      </div>
      <div className="text-[10px] text-text-muted">
        {savesToClient ? "Se guardará en los destinos del cliente." : "El cliente ya tiene 5 destinos: este destino se usará solo en esta factura."}
      </div>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="px-3 py-1 text-xs border border-border rounded-md">Cancelar</button>
        <button type="button" data-testid="nuevo-destino-guardar"
          onClick={() => { if (!d.alias.trim() || !d.direccion.trim()) { toast.error("Alias y dirección son obligatorios"); return; } onSave(d); }}
          className="px-3 py-1 text-xs bg-agro-green text-black font-medium rounded-md">Usar destino</button>
      </div>
    </div>
  );
}

const Lbl = ({ children }) => <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">{children}</label>;
const TR = ({ l, v }) => <div className="flex justify-between"><span className="text-text-secondary text-xs">{l}</span><span className="text-text-primary">{v}</span></div>;
const Chip = ({ label, value, accent, warning }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border ${
    warning ? "bg-agro-yellow/10 text-agro-yellow border-agro-yellow/30" :
    accent ? "bg-agro-green/10 text-agro-green border-agro-green/30" :
    "bg-app border-border text-text-secondary"
  }`}>
    <span className="text-[9px] uppercase tracking-wider opacity-70">{label}:</span>
    <span className="font-medium">{value}</span>
  </span>
);
